/**
 * Angular Component. Checkbox with BMAT Style
 */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface BMSwitchCheckboxChangeEvent {
  source: BMSwitchCheckboxComponent;
  checked: boolean;
}

@Component({
  selector: 'bm-switch-checkbox',
  templateUrl: 'bm-switch-checkbox.component.html',
  styleUrls: ['bm-switch-checkbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // tslint:disable-next-line:no-forward-ref
      useExisting: forwardRef(() => BMSwitchCheckboxComponent),
      multi: true,
    },
  ],
})
export class BMSwitchCheckboxComponent implements ControlValueAccessor {
  @ViewChild('input', { static: true }) public input!: ElementRef<HTMLInputElement>;
  @HostBinding('class.bm-switch-checkbox--focused')
  public get isFocused() {
    return this._focused;
  }

  /**
   * Input values to directly copy to inner checkbox
   */
  @Input('aria-label') public ariaLabel!: string;
  @Input('aria-labelledby') public ariaLabelledby!: string;
  @Input() public disabled!: boolean;
  @Input() public name!: string;
  @Input() public readonly!: boolean;
  @Input() public required!: boolean;
  @Input() public tabIndex = 0;

  @Input()
  public set checked(checked: boolean) {
    this._renderer.setProperty(this.input.nativeElement, 'checked', checked);
  }
  public get checked() {
    if (this.input && this.input.nativeElement) {
      return this.input.nativeElement.checked;
    }
    return false;
  }

  @Output() public switchChange = new EventEmitter<
    BMSwitchCheckboxChangeEvent
  >();

  private _focused!: boolean;

  private _propagateChange!: (_: boolean) => void;
  private _onTouched!: () => void;

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _renderer: Renderer2,
  ) {}

  // Focus Blur Handle
  public inputFocus() {
    this._focused = true;
  }

  public inputBlur() {
    this._focused = false;

    if (this._onTouched) {
      this._onTouched();
    }
  }

  public writeValue(value: boolean) {
    this.checked = value;
    this._changeDetectorRef.markForCheck();
  }

  public registerOnChange(fn: (_: boolean) => {}) {
    this._propagateChange = fn;
  }

  public registerOnTouched(fn: () => void) {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this._changeDetectorRef.markForCheck();
  }

  public inputChange(event: Event) {
    if (this._propagateChange) {
      this._propagateChange((event.target as HTMLInputElement).checked);
    }

    this.switchChange.emit({
      checked: (event.target as HTMLInputElement).checked,
      source: this,
    });
  }

  public focus() {
    if (this.input && this.input.nativeElement) {
      this.input.nativeElement.focus();
    }
  }
}
